// tslint:disable:no-switch-case-fall-through
// Needed because tslint gets confused by a block scope on a case where it always returns within that block.

import {
  assign,
  defaults,
  filter,
  find,
  isNaN,
  map,
  reject,
  some,
  sortBy,
  uniqBy,
} from 'lodash-es';
import * as Redux from 'redux';
import { produce, setUseProxies } from 'immer';

import AptAssessmentVote from '../../../core/components/aptAssessmentVote';
import {
  recalculateAndRefilter,
  updateProductCalculations,
} from './calculations';
import { generateDefaultFilters } from './filters/FilterBase';
import { savings, savingsPercentage } from './shared-calculations';
import {
  allProducts,
  findCross,
  findIncumbentProduct,
  findProductCross,
  getAllocations,
  incumbentAndCrosses,
  isLocked,
  justCrosses,
} from './stateLocators';
import { mapTiers, updateAllocations } from './stateModifiers';
import { CrossReferencesOpinionOption } from '../../../../../../../../../../frontend/libs/shared/src/lib/types/product/crossess-refernces';
import { Features } from '../../../../../../../../../../frontend/libs/shared/src';

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Note: To understand this code is to understand how Redux works. So it's really worth taking an hour or so and
// going through the Redux docs (http://redux.js.org/index.html) to get what's being done here.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// The most basic actions are those which go get data from an API and then load it into our data model.
const FETCH_ACCOUNT = 'Fetch Account';
const FETCH_RFP = 'Fetch RFP';
const LOAD_ACCOUNT = 'Load Account';
const LOAD_RFP = 'Load RFP';

const FETCH_ACTIVE_PURCHASE_PLAN = 'Fetch Active Purchase Plan';
const LOAD_ACTIVE_PURCHASE_PLAN = 'Load Active Purchase Plan';

const FETCH_MICRO_CHART_DATA_FOR_OFFER = 'Fetch Micro Chart Data for Offer';
const UPDATE_MICRO_CHART_DATA_FOR_OFFER = 'Update Micro Chart Data for Offer'; //old endpoint will be deprecated soon
const UPDATE_MICRO_CHART_DATA_FOR_OFFERS = 'Update Micro Chart Data for Offers'; // New endpoint

const FETCH_COVERAGE_SUMMARY = 'Fetch Coverage Summary';
const LOAD_COVERAGE_SUMMARY = 'Load Coverage Summary';
const UPDATE_LOCK_STATUS_FOR_OFFERS = 'Update Lock Status for Offers';

const FETCH_PRODUCTS = 'Fetch Products';
const LOAD_PRODUCTS = 'Load Products';

// Then we've got those associated with user actions on the page (for example, moving allocation from one product to
// another or changing our terms).
const ADD_NOTE = 'Add Note';
const FILTER = 'Filter';
const MASS_LOCK = 'Mass Lock';
const MASS_MOVE_ALLOCATION = 'Mass Move Allocation';
const MOVE_ALLOCATION = 'Move Allocation';
const OPEN_LOCKS = 'Open Locks';
const REMOVE_CROSS_FROM_PRODUCT = 'Remove Cross from Product';
const SAVED = 'Saved';
const UPDATE_LOCK = 'Update Lock';
const UPDATE_TERMS = 'Update Terms';
const VOTE = 'Vote';
const ADD_CROSS_TO_PRODUCT = 'Add Cross To Product';
const FETCH_FEATURES = 'features list';

const FORMULARY = 'formulary';
const NON_FORMULARY = 'nonformulary';
const NO_OPINION = 'no_opinion';

interface Allocation {
  productId: number;
  allocationPct: number;
}

export class DetailsModel {
  featureList: any = {};

  defaultState = {
    fetching: {
      account: true,
      rfp: false,
      activePurchasePlan: false,
      coverageSummary: false,
      products: false,
    },

    counts: {
      approved: 0,
      disapproved: 0,
      excludedFromCompliance: 0,
      includedInCompliance: 0,
      incumbents: 0,
      locked: 0,
      matches: 0,
      noDecision: 0,
      unlocked: 0,
      maxVariance: 0,
      formularyPricing: 0,
      nonFormularyPricing: 0,
    },

    filters: {}, // We'll get the default state of this from a generator function.

    account: {},
    rfp: {},
    activePurchasePlan: {},
    offers: [],
    products: [],
    unspscs: [],

    ///////////////
    // Derived data
    //
    // I really hate this. I don't think I'll ever do another Redux model where I put Angular UI Grid or
    // Wijmo data into the model itself. I'm now 100% convinced that there must be better ways to get this data
    // than manipulating it when we're actually dealing with the list of products.
    ///////////////
    uiGridProducts: [],

    // This holds the same data as uiGridProducts but with any user filters applied to it. So it will often be many
    // fewer lines.
    filteredUiGridProducts: [],
    featureList: {},
  };

  store;

  ////////////////////////////////////////////////////////////////////////////////
  // Reducer - Again, it's worth reading through the Redux docs to understand why
  // these are structured as they are.
  ////////////////////////////////////////////////////////////////////////////////
  reducer(state: any, action: any) {
    // Note: When this function is called by Redux, "this" will not point to anything useful to you. Every action must
    // come with all the data it needs in order to be able to do its job.

    let newState = state;

    let refilter = false;
    let recalculateProductNumbers = false;
    let recalculateUnspscNumbers = false;
    let recalculateOfferNumbers = false;
    let recalculatePlanNumbers = false;

    switch (action.type) {
      case FETCH_ACCOUNT:
        newState = produce(state, (draft) => {
          draft.fetching.account = true;
        });
        break;
      case FETCH_ACTIVE_PURCHASE_PLAN:
        newState = produce(state, (draft) => {
          draft.fetching.activePurchasePlan = true;

          // If we're viewing the purchase plan for a particular member we should remember the number just in case we
          // have to refresh the data.
          if (action.data.memberId) {
            draft.activePurchasePlan.memberId = action.data.memberId;
          }
        });
        break;
      case FETCH_COVERAGE_SUMMARY:
        newState = produce(state, (draft) => {
          draft.fetching.coverageSummary = true;
        });
        break;
      case FETCH_PRODUCTS:
        newState = produce(state, (draft) => {
          draft.fetching.products = true;
        });
        break;
      case FETCH_RFP:
        newState = produce(state, (draft) => {
          draft.fetching.rfp = true;
        });
        break;
      case FILTER:
        // This is a precursor to putting the filtering operation into the various switches which require it after they
        // have performed their action. Each one can just set refilter to true and it will happen automatically without
        // the action functions having to call for it.
        newState = produce(state, (draft) => {
          refilter = true;

          draft.filters = action.data;
        });
        break;
      case LOAD_ACCOUNT:
        newState = produce(state, (draft) => {
          draft.fetching.account = false;

          draft.account = action.data;
        });
        break;
      case LOAD_ACTIVE_PURCHASE_PLAN:
        newState = produce(state, (draft) => {
          draft.fetching.activePurchasePlan = false;

          draft.activePurchasePlan = assign(
            {},
            draft.activePurchasePlan,
            action.data,
            {
              savings: savings(
                action.data.continuedSpend,
                action.data.plannedSpend,
              ),
              savingsPercentage: savingsPercentage(
                action.data.continuedSpend,
                action.data.plannedSpend,
              ),
            },
          );

          let offers = [
            {
              id: 'incumbent',
              seller: {
                name: 'Incumbent',
              },
            },
          ];

          // Note: First we filter out unselected offers and then we map offers with a small amount of added data.
          let convertedOffers = map(
            filter(action.data.offers, { selected: true }),
            (offer: any) => {
              // Find the offer information attached to the RFP (if available) and put it's name here so it's with the
              // rest of the data.
              // tslint:disable-next-line:whitespace (whitespace check hates <any>variable)
              let rfpOffer = find(draft.rfp.offers, { id: offer.id }) as any;

              return assign({}, offer, {
                name: rfpOffer ? rfpOffer.name : offer.name ? offer.name : '',
              });
            },
          );

          // Note that we're resorting the offers into order based upon the seller name. That means we also need to
          // change the sort order of other offer information (for example, coverage bars at the UNSPSC level).
          draft.offers = offers.concat(
            sortBy(convertedOffers, (offer: any) => offer.seller.name),
          );
        });
        break;
      case LOAD_RFP:
        newState = produce(state, (draft) => {
          draft.fetching.rfp = false;

          draft.rfp = action.data;
        });
        break;
      case LOAD_COVERAGE_SUMMARY:
        newState = produce(state, (draft) => {
          draft.fetching.coverageSummary = false;

          let unspscs = action.data.coverageByUnspsc;

          draft.unspscs = sortBy(
            unspscs,
            (unspsc: any) => unspsc.continuedSpend,
          ).reverse();
        });
        break;
      case LOAD_PRODUCTS:
        let products = action.data.products?.products ?? [];

        // Sort the products by planned spend.
        products.sort((a, b) => {
          // Do all calculations and then sort by aggregate planned spend number.
          let psA = updateProductCalculations(a).calculations.plannedSpend;
          let psB = updateProductCalculations(b).calculations.plannedSpend;

          if (psA < psB) {
            return 1;
          } else if (psA > psB) {
            return -1;
          } else {
            // a must be equal to b
            return 0;
          }
        });

        // Note: This goes with the MBV-1030 removed stuff below. It has a pretty high likelihood of
        // coming back.
        // let allUnspscIds = state.unspscs.map((unspsc) => unspsc.unspsc.id);

        // This is a process where we pre-calculate stuff or add additional data to the products so it doesn't
        // get done over and over again later.
        products.forEach((product) => {
          // Add an incumbentProductId to each incumbent product. They all have a product ID,
          // which is logical, but it leaves them inconsistent with all the other products.
          product.incumbentProduct.incumbentProductId =
            product.incumbentProduct.productId;

          // Formulary Pricing Logic
          product.formularyPricingType = NO_OPINION;

          let totalUsedIncumbentPrice = 0;

          let isFormulary = product.crossedProductsByOffer.some(
            (cross: any, index: number) => {
              let incumbentAndCrossesObj = justCrosses(
                product,
                cross.offerId,
              ).next().value;
              if (
                incumbentAndCrossesObj &&
                incumbentAndCrossesObj.product &&
                (incumbentAndCrossesObj.product.usedIncumbentPrice ||
                  !incumbentAndCrossesObj.product.unitPrice)
              ) {
                totalUsedIncumbentPrice++;
              }

              return (
                incumbentAndCrossesObj &&
                incumbentAndCrossesObj.incumbent &&
                incumbentAndCrossesObj.product &&
                incumbentAndCrossesObj.incumbent.targetPrice &&
                !incumbentAndCrossesObj.product.usedIncumbentPrice &&
                incumbentAndCrossesObj.product.unitPrice &&
                incumbentAndCrossesObj.product.unitPrice <=
                incumbentAndCrossesObj.incumbent.targetPrice
              );
            },
          );

          if (product.incumbentProduct.targetPrice && isFormulary) {
            product.formularyPricingType = FORMULARY;
          } else if (
            product.incumbentProduct.targetPrice &&
            !isFormulary &&
            totalUsedIncumbentPrice === 0
          ) {
            product.formularyPricingType = NON_FORMULARY;
          }

          // I love using the generator which iterates all of the products related to an incumbent. It makes all of
          // this kind of code so much easier.
          for (let value of incumbentAndCrosses(product)) {
            value.product.notes = value.product.crossOpinionNotes;
            value.product.targetPrice = product.incumbentProduct.targetPrice;
            delete value.product.crossOpinionNotes;

            value.product.searchText = ''
              .concat(value.product.description, value.product.catalogNumber)
              .toLowerCase();

            // Note: This rule was killed by MBV-1030, but it might come back in another form. So we may
            // end up putting a different boolean onto the product to indicate that it is outside the RFP
            // and then displaying an appropriate marker of some kind in UI.
            //
            // In addition to excluding carved in/out products, if a product is crossed in from outside the RFP, we treat
            // that the same way.
            // let excludeProductOutsideRfp =
            //   'incumbent' in value &&
            //   value.product.unspsc.id !== value.incumbent.unspsc.id &&
            //   !allUnspscIds.includes(value.product.unspsc.id);

            value.product.exclude =
              value.product.spendStatus === 'CARVE_IN' ||
              value.product.spendStatus === 'CARVE_OUT';
          }
        });

        newState = produce(state, (draft) => {
          draft.fetching.products = false;
          recalculateProductNumbers = true;

          draft.products = products;
        });
        break;
      case MASS_LOCK:
        newState = produce(state, (draft) => {
          // If we change the status of a lock, then we may need to refilter.
          refilter = true;

          for (let [
            incumbentProductId,
            toProductId,
          ] of action.data.lockMap.entries()) {
            // findIncumbentProduct is a bit of a misnomer. It finds the product and all of its crosses,
            // but it does so using the incumbent product ID.
            let product = findIncumbentProduct(draft, incumbentProductId);
            let updatedProduct = findCross(product, toProductId);

            // This kind of uses a weird default behavior. If findCross() found a cross, we update it.
            // If it didn't find a cross, the thing we must be talking about is the incumbent (that's
            // really just a baked in assumption).
            if (updatedProduct) {
              updatedProduct.lockedAllocation = action.data.locked;
            } else {
              product.incumbentProduct.lockedAllocation = action.data.locked;
            }
          }
        });
        break;
      case MASS_MOVE_ALLOCATION:
        newState = produce(state, (draft) => {
          recalculateProductNumbers = true;

          draft.activePurchasePlan.planChanged = true;

          for (let [
            incumbentProductId,
            toProductId,
          ] of action.data.moveMap.entries()) {
            let product = findIncumbentProduct(draft, incumbentProductId);

            for (let value of incumbentAndCrosses(product)) {
              // If it's a cross and that cross is the target for the allocation.
              if (
                'incumbent' in value &&
                value.product.productId === toProductId
              ) {
                value.product.allocation = 100;
              } else {
                value.product.allocation = 0;
              }
            }
          }
        });
        break;
      case MOVE_ALLOCATION:
        newState = produce(state, (draft) => {
          recalculateProductNumbers = true;

          draft.activePurchasePlan.planChanged = true;

          let product = findIncumbentProduct(
            draft,
            action.data.incumbentProductId,
          );

          updateAllocations(product, action.data.allocations);
        });
        break;
      case OPEN_LOCKS:
        newState = produce(state, (draft) => {
          refilter = true;

          draft.activePurchasePlan.planChanged = true;

          // Iterate through all of the filtered products and clear locks only on those.
          draft.filteredUiGridProducts.forEach((filteredProduct) => {
            let product = findIncumbentProduct(
              draft,
              filteredProduct.incumbentProductId,
            );

            // Clear any locks on the incumbent.
            product.incumbentProduct.lockedAllocation = false;

            // Then any on any off the crossed products.
            product.crossedProductsByOffer.forEach((offer: any) => {
              offer.crossedProducts.forEach((product: any) => {
                product.lockedAllocation = false;
              });
            });
          });
        });
        break;
      case REMOVE_CROSS_FROM_PRODUCT:
        newState = produce(state, (draft) => {
          let product = findIncumbentProduct(
            draft,
            action.data.incumbentProductId,
          );

          // We are guaranteed to have an some crossed products because we are removing one which already exists.
          product.crossedProductsByOffer = product.crossedProductsByOffer.map(
            (offer) => {
              offer.crossedProducts = offer.crossedProducts.filter(
                (cross: any) => {
                  return cross.productId !== action.data.productToRemove;
                },
              );

              return offer;
            },
          );
        });
        break;
      case SAVED:
        newState = produce(state, (draft) => {
          draft.activePurchasePlan.name = action.data.planName;
          draft.activePurchasePlan.planChanged = false;
        });
        break;
      case UPDATE_LOCK:
        newState = produce(state, (draft) => {
          // If we changed the status of a lock, then we may need to refilter.
          refilter = true;
          draft.activePurchasePlan.planChanged = true;

          let product = findIncumbentProduct(
            draft,
            action.data.incumbentProductId,
          );

          let updatedProduct = findCross(product, action.data.toProductId);

          if (updatedProduct) {
            updatedProduct.lockedAllocation = action.data.locked;
          } else {
            product.incumbentProduct.lockedAllocation = action.data.locked;
          }
        });
        break;
      case UPDATE_LOCK_STATUS_FOR_OFFERS:
        newState = produce(state, (draft) => {
          recalculateOfferNumbers = true;

          // add/update the lock status for each offer
          draft.offers = map(draft.offers, (offer: any) => {
            let lockStatus =
              find(action.data.lockStatusByOffer, { offerId: offer.id }) || {};
            return assign({}, offer, { lockStatus });
          });
        });
        break;
      case UPDATE_MICRO_CHART_DATA_FOR_OFFER:
        newState = produce(state, (draft) => {
          const offer = find(draft.offers, { id: action.data.offerId }) as any;
          offer.microChartData = action.data.microChartData;
        });
        break;
      case UPDATE_MICRO_CHART_DATA_FOR_OFFERS:
        newState = produce(state, (draft) => {
          draft.offers = map(draft.offers, (offer: any) => {
            const offerTier = find(action.data.offersTiers, { offerId: offer.id }) as any;
            if (offerTier) {
              const mappedTiers = mapTiers(offerTier.tiers);
              offer.microChartData = {
                tiers: mappedTiers
              }
            }
            return offer;
          });
        });
        break;
      case UPDATE_TERMS:
        newState = produce(state, (draft) => {
          draft.activePurchasePlan.planChanged = true;
        });
        break;
      case VOTE:
        newState = produce(state, (draft) => {
          // It seems like this would just be a refiltering operation, but if the user disputed a cross,
          // we may have changed allocations.
          recalculateProductNumbers = true;

          let targetProductCross = findProductCross(
            draft,
            action.data.productId,
            action.data.crossReferenceId,
          );
          if (targetProductCross) {
            targetProductCross.parentOpinion =
              action.data.currentOpinion.targetProductCrossParentOpinion;
            targetProductCross.crossOpinion =
              action.data.currentOpinion.targetProductCrossCrossOpinion;
            targetProductCross.crossApproveCount =
              action.data.currentOpinion.targetProductCrossCrossApproveCount;
            targetProductCross.crossDisputeCount =
              action.data.currentOpinion.targetProductCrossCrossDisputeCount;
            targetProductCross.notes =
              action.data.currentOpinion.targetProductCrossNotes;
          }
        });
        break;
      case ADD_NOTE:
        newState = produce(state, (draft) => {
          let targetProductCross = findProductCross(
            draft,
            action.data.productId,
            action.data.crossReferenceId,
          );

          targetProductCross.notes.push(action.data.notes);
        });
        break;
      case ADD_CROSS_TO_PRODUCT:
        newState = produce(state, (draft) => {
          refilter = true;

          let product = findIncumbentProduct(
            draft,
            action.data.incumbentProductId,
          );

          // We are defaulting this properties of a cross for now since backend is not returning these in the addCross post.
          let defaultValues = {
            allocation: 0,
            crossReferenceCreatedByOrgType: 'Buyer',
            isOnCa: null,
            lockedAllocation: false,
            packages: null,
            selected: null,
            usedIncumbentPrice: false,
          };

          let productToAdd = defaults(
            action.data.productCrossToAdd,
            defaultValues,
          );

          // These transformations were necessary since the cross data scheme is slightly different from the result of the addCross api and the crosses in ppDetails.
          productToAdd.status = productToAdd.productStatus;
          productToAdd.productId = productToAdd.id;
          productToAdd.pricingRequired = productToAdd.unitPrice ? false : true;
          productToAdd.incumbentProductId = action.data.incumbentProductId;

          // We check that we have an offer for the seller we are about to cross a product to.
          let offer = find(draft.offers, (offer) => {
            return offer.seller.id === productToAdd.seller.id;
          });

          if (offer) {
            // We find the product offer that matches the seller of the product to add. CQVA-433 - Started searching
            // using the offer ID because the back-end wasn't returning the seller ID as part of the
            // crossedProductsByOffer anymore.
            let offerToAddProductCrossTo = find(
              product.crossedProductsByOffer,
              (crossedProductByOffer) => {
                return crossedProductByOffer.offerId === offer.id;
              },
            );

            // We add new crossed to existing product crosses array if we have found an offer with a matching seller
            // (offerToAddProductCrossTo).
            if (offerToAddProductCrossTo) {
              offerToAddProductCrossTo.crossedProducts.push(productToAdd);
            } else {
              // We created this new offer object so that we can push it to the existing data structure in the cases
              // where we had offers with zero product crosses.
              let newOfferWithCrossedProduct = {
                offerId: offer.id,
                seller: productToAdd.seller,
                crossedProducts: [productToAdd],
              };

              product.crossedProductsByOffer.push(newOfferWithCrossedProduct);
            }
          }
        });
        break;
      case FETCH_FEATURES:
        newState = produce(state, (draft) => {
          draft.featureList = action.data.featureList;
        });
        break;
    }

    return recalculateAndRefilter(
      newState,
      action,
      recalculateProductNumbers,
      recalculateUnspscNumbers,
      recalculateOfferNumbers,
      recalculatePlanNumbers,
      refilter,
    );
  }

  fetchingPromise: Promise<void> = null;
  resolve: () => void;

  fetching(state: any) {
    // If any of the fetching flags are true, this will return a promise. It's an easy way to be sure the loading
    // indicator shows until we've retrieved everything of importance. Any call we don't care about blocking the user
    // for, we can just not put in a fetching flag for that...
    let retVal = some(state.fetching);

    // If we already have an existing promise, we're most concerned with when it resolves.
    if (this.fetchingPromise) {
      // If nothing is fetching anymore, we should resolve the existing promise for anybody who is watching
      // it, and then get rid of it so we'll create a new promise next time fetching starts.
      if (!retVal) {
        this.resolve();
        this.fetchingPromise = null;
      }
    } else {
      // If we don't have an existing promise and we have some fetching happening, then we will create a new promise
      // and return it. We'll also save off the resolve function so we can use it later.
      if (retVal) {
        this.fetchingPromise = new Promise((resolve) => {
          this.resolve = resolve;
        });
      }
    }

    return this.fetchingPromise;
  }

  filtering(state: any) {
    return some(state.filters);
  }

  constructor(private api, private chartGenerationService) {
    'ngInject';

    (this as any).defaultState.filters = generateDefaultFilters();

    // Create a Redux store to manipulate the state of this view: http://redux.js.org/index.html
    this.store = Redux.createStore(
      this.reducer,
      // tslint:disable-next-line:whitespace (whitespace check hates <any>variable)
      (this as any).defaultState,
      // tslint:disable-next-line:whitespace (whitespace check hates <any>variable)
      (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
      // tslint:disable-next-line:whitespace (whitespace check hates <any>variable)
      (window as any).__REDUX_DEVTOOLS_EXTENSION__(),
    );
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Actions - These are what outside code will call to trigger model changes.
  // Outside code does not dispatch actions to the Redux store, it calls these
  // functions or reads values from the state.
  ////////////////////////////////////////////////////////////////////////////////
  fetchRfp(rfpId: string) {
    let loadRfp = this.api.rfps.getRfp(rfpId);

    this.store.dispatch({ type: FETCH_RFP });

    return loadRfp.then((rfp) => {
      // Pass the returned data to the model for processing.
      this.loadRfp(rfp);

      return rfp;
    });
  }

  fetchAccount() {
    let loadAccount = this.api.users.account('me');

    this.store.dispatch({ type: FETCH_ACCOUNT });

    loadAccount.then((account) => {
      this.loadAccount(account);
    });

    return loadAccount;
  }

  loadAccount(data) {
    this.store.dispatch({ type: LOAD_ACCOUNT, data: data });
  }

  loadRfp(data) {
    this.store.dispatch({ type: LOAD_RFP, data: data });
  }

  fetchActivePurchasePlan(rfpId: string, memberId?: string) {
    let loadActivePurchasePlan = this.api.rfps.getActivePurchasePlan(
      rfpId,
      memberId,
    );

    this.store.dispatch({
      type: FETCH_ACTIVE_PURCHASE_PLAN,
      data: {
        memberId,
      },
    });

    loadActivePurchasePlan.then((activePurchasePlan) => {
      this.fetchCoverageSummary(activePurchasePlan.id);

      // Pass the returned data to the model for processing.
      this.loadActivePurchasePlan(rfpId, activePurchasePlan);

      // this process is dependent of loadActivePurchasePlan having already
      // added offers to the current state so order of execution matters
      this.fetchMicroChartDataForOffers();
    });

    return loadActivePurchasePlan;
  }

  loadActivePurchasePlan(rfpId: string, activePurchasePlan) {
    this.store.dispatch({
      type: LOAD_ACTIVE_PURCHASE_PLAN,
      data: activePurchasePlan,
    });
  }

  // iterate through each offer and request its micro-chart data
  // and asynchronously dispatch event to add the data to the offer
  fetchMicroChartDataForOffers() {
    this.store.dispatch({ type: FETCH_MICRO_CHART_DATA_FOR_OFFER });

    const state = this.store.getState();
    const offers = reject(state.offers, { id: 'incumbent' }) || [];

    if ((state.rfp?.status?.toUpperCase() === 'DRAFT') ||
      (this.featureList.MIGRATE_TIER_ATTAINABILITY_PP_956458)) {
      this.api.purchasePlans.getOffersTiers(state.activePurchasePlan?.id).then((response) => {
        this.updateMicroChartDataForOffers(response);
      });
    }
    else { // Will be deprecated soon
      offers.forEach((offer) => {
        const usePrimaryEquivalentCrosses = null;

        let partnerId;

        if (
          state.rfp.groupRfp &&
          (state.account.isSystem ||
            (state.account.isLeader && state.activePurchasePlan.memberId))
        ) {
          if (state.activePurchasePlan.memberId) {
            partnerId = state.activePurchasePlan.memberId;
          } else {
            partnerId = state.account.organization.id;
          }
        }

        // load microChart data for a specific offer and dispatch event to add the data to the offer
        // we are ok with the UI updating the charts one at a time as the data is received
        this.api.offers
          .getMicrochart(offer.id, partnerId, usePrimaryEquivalentCrosses, {
            cache: true,
          })
          .then((microChartData) => {
            this.updateMicroChartDataForOffer(microChartData, offer.id);
          });
      });
    }
  }

  updateMicroChartDataForOffer(microChartData, offerId) {
    this.store.dispatch({
      type: UPDATE_MICRO_CHART_DATA_FOR_OFFER,
      data: {
        microChartData,
        offerId,
      },
    });
  }

  updateMicroChartDataForOffers(offersTiers) {
    this.store.dispatch({
      type: UPDATE_MICRO_CHART_DATA_FOR_OFFERS,
      data: {
        offersTiers
      },
    });
  }

  fetchCoverageSummary(planId: number) {
    this.store.dispatch({ type: FETCH_COVERAGE_SUMMARY });

    // This is our first dependent call. We can't make this one until we know about the active purchase plan.
    let loadCoverageSummary = this.api.purchasePlans.getCoverageSummary(planId);

    return loadCoverageSummary.then((coverageSummary) => {
      // Pass the returned data to the model for processing.
      this.loadCoverageSummary(coverageSummary);
    });
  }

  loadCoverageSummary(coverageSummary) {
    // Load products for all of the UNSPSCs.
    this.fetchProducts();

    this.store.dispatch({ type: LOAD_COVERAGE_SUMMARY, data: coverageSummary });
    this.store.dispatch({
      type: UPDATE_LOCK_STATUS_FOR_OFFERS,
      data: coverageSummary,
    });
  }

  fetchProducts() {
    let state = this.store.getState();

    // Note that we're loading product info on the model.
    this.store.dispatch({ type: FETCH_PRODUCTS });

    this.api.purchasePlans
      .getAllProducts(state.activePurchasePlan.id)
      .then((response) => {
        this.loadProducts(response);

        // only return length to not expose data objects
        // Why am I returning anything at all here? - JM
        return response?.products.length;
      });
  }

  loadProducts(data) {
    this.store.dispatch({
      type: LOAD_PRODUCTS,
      data: {
        products: data,
      },
    });
  }

  updateTerms(
    offerId: number,
    pricingType: string,
    duration: number,
    tierId: number,
    isReoptimize: boolean = false,
  ) {
    let state = this.store.getState();

    if (isReoptimize) {
      this.api.purchasePlans
        .changeTier(state.activePurchasePlan.id, offerId, tierId)
        .then(() => {
          // Update the terms for this offer.
          this.store.dispatch({
            type: UPDATE_TERMS,
            data: {
              offerId,
              pricingType,
              duration,
            },
          });

          // Reload some of the same state we loaded when we first came up.
          this.fetchActivePurchasePlan(
            state.rfp.id,
            state.activePurchasePlan.memberId,
          );
        });
    } else {
      // Make the API call to change the terms.
      this.api.purchasePlans
        .updateOfferTerms(
          state.activePurchasePlan.id,
          offerId,
          pricingType,
          duration,
          tierId,
        )
        .then(() => {
          // Update the terms for this offer.
          this.store.dispatch({
            type: UPDATE_TERMS,
            data: {
              offerId,
              pricingType,
              duration,
            },
          });
          // Reload some of the same state we loaded when we first came up.
          this.fetchActivePurchasePlan(
            state.rfp.id,
            state.activePurchasePlan.memberId,
          );
        });
    }
  }

  saved(newPlan) {
    this.store.dispatch({ type: SAVED, data: newPlan });
  }

  moveUnlockedAllocationsToOffer(offerId) {
    let state = this.store.getState();
    let moveMap = new Map<number, Allocation[]>();

    // Get a list of all the visible products and then filter down to those to which we can allocate.
    state.products.forEach((product) => {
      let newProductId = null;
      let newProductPrice = Number.MAX_VALUE;

      // Is it visible and not already locked allocation?
      if (product.visible && !isLocked(product)) {
        // Iterate over just the crosses for the offer in question.
        for (let value of justCrosses(product, offerId)) {
          // We don't want to allocate to it if they've disputed the cross or if it's not priced.
          if (
            value.product.crossOpinion !== 'dispute' &&
            !value.product.pricingRequired
          ) {
            // Note: This is going to favor a higher ranked cross over a lower one (for example,
            // primary over secondary) but overrule that if the price is better.
            if (value.product.unitPrice < newProductPrice) {
              newProductId = value.product.productId;
              newProductPrice = value.product.unitPrice;
            }
          }
        }

        // If we found something to which we can allocate, we'll add it as a move.
        if (newProductId !== null) {
          moveMap.set(product.incumbentProduct.productId, newProductId);
        }
      }
    });

    this.store.dispatch({
      type: MASS_MOVE_ALLOCATION,
      data: {
        moveMap,
      },
    });

    return this.api.purchasePlans.massAllocate(
      state.activePurchasePlan.id,
      offerId,
      Array.from(moveMap.keys()),
    );
  }

  massLockForOfferAllocations(offerId, locked) {
    let state = this.store.getState();
    let lockMap = new Map<number, number>();

    // Get a list of all of the visible products and filter down to those with allocations for the
    // specific offer.
    state.products.forEach((product) => {
      // Is it visible?
      if (product.visible) {
        // Does it have 100% allocation to a product from the offer in question?
        for (let value of justCrosses(product, offerId)) {
          if (value.product.allocation === 100) {
            lockMap.set(
              product.incumbentProduct.productId,
              value.product.productId,
            );
          }
        }
      }
    });

    this.store.dispatch({
      type: MASS_LOCK,
      data: {
        lockMap,
        locked,
      },
    });

    return this.api.purchasePlans.lockAndUnlockLocks(
      state.activePurchasePlan.id,
      locked,
      Array.from(lockMap.keys()),
    );
  }

  /**
   * This is the kind of move we trigger when the user drags and drops allocation from one
   * product to another in the UI. Or when we're removing allocation, we just don't specify a
   * destination.
   */
  moveAllocation(
    incumbentProductId: number,
    fromProductId: number,
    toProductId: number,
    callApi: boolean = true,
  ) {
    let state = this.store.getState();

    // Translate this to a reallocation which sets the "from" product to zero allocation,
    // the "to" product to its current allocation plus the amount on the "from" product, and
    // which leaves all the other products alone.
    let product = findIncumbentProduct(state, incumbentProductId);

    let allocations = getAllocations(product);

    let allocationAmountToBeMoved = (
      find(allocations as any, {
        // tslint:disable-next-line:whitespace (whitespace check hates <any>variable)
        productId: fromProductId,
      }) as any
    ).allocationPct;

    let reallocations = map(allocations, (allocation: any) => {
      if (allocation.productId === fromProductId) {
        return {
          allocationPct: 0,
          productId: allocation.productId,
        };
      } else if (allocation.productId === toProductId) {
        return {
          allocationPct: allocation.allocationPct + allocationAmountToBeMoved,
          productId: allocation.productId,
        };
      } else {
        // Keep the existing allocation percentage.
        return {
          allocationPct: allocation.allocationPct,
          productId: allocation.productId,
        };
      }
    });

    // Now that we have a list of all the product IDs and the allocation percentage for each one, we can make a call
    // to a function which will update the state with that information.
    this.updateAllocations(incumbentProductId, reallocations, callApi);
  }

  /**
   * This sets the allocation to 0% for a given product. One way it gets used is when a user down votes a
   * cross and we want to remove any allocation from that product. Note: This function does not invoke the
   * API call which updates the back-end.
   *
   * @param incumbentProductId
   * @param fromProductId
   */
  removeAllocation(incumbentProductId: number, fromProductId: number) {
    this.moveAllocation(incumbentProductId, fromProductId, null, false);
  }

  setAllocation(
    incumbentProductId: number,
    toProductId: number,
    allocationPct: number,
  ) {
    let state = this.store.getState();

    let product = findIncumbentProduct(state, incumbentProductId);

    let allocations = getAllocations(product);

    let leftoverAllocationPct = 100 - allocationPct;

    let reallocations = map(allocations, (allocation: any) => {
      if (allocation.productId !== toProductId) {
        return {
          allocationPct: Math.min(
            allocation.allocationPct,
            leftoverAllocationPct,
          ),
          productId: allocation.productId,
        };
      } else {
        return {
          allocationPct: allocationPct,
          productId: allocation.productId,
        };
      }
    });

    this.updateAllocations(incumbentProductId, reallocations, true);
  }

  // This function allows us to change the allocation on all the products associated with a given incumbent product.
  // It is called from the code which does simple drag and drop reallocations as well as it can be called after the
  // user has updated across the board in the dialog we provide for changing allocation.
  updateAllocations(
    incumbentProductId: number,
    reallocations,
    callApi: boolean = false,
  ) {
    let state = this.store.getState();

    if (callApi) {
      this.api.purchasePlans.updatePlanAllocations(
        state.activePurchasePlan.id,
        incumbentProductId,
        reallocations,
      );
    }

    // This actually reset the product and its associate crosses to have new allocations.
    this.store.dispatch({
      type: MOVE_ALLOCATION,
      data: {
        incumbentProductId,
        allocations: reallocations,
      },
    });
  }

  setFeatureList(featureList) {
    this.featureList = featureList;
    this.store.dispatch({
      type: FETCH_FEATURES,
      data: { featureList },
    });
  }

  openLocks() {
    let state = this.store.getState();

    this.store.dispatch({
      type: OPEN_LOCKS,
    });

    // We get the list of unique product ids so that we can make a call to unlock all locked products.
    // Because this uses the list of uiGridProducts (which are filtered), it chooses the right ones to
    // unlock.
    let fromProducts = uniqBy(state.uiGridProducts, 'incumbentProductId');

    let filteredProductIdsList = filter(fromProducts, (product) => {
      return product.lockedAllocation === true;
    });

    filteredProductIdsList = map(filteredProductIdsList, (product) => {
      return product.incumbentProductId;
    });

    this.api.purchasePlans.lockAndUnlockLocks(
      state.activePurchasePlan.id,
      false,
      filteredProductIdsList,
    );
  }

  toggleLock(incumbentProductId, toProductId) {
    let state = this.store.getState();

    let product = findIncumbentProduct(state, incumbentProductId);

    let locked = !isLocked(product);
    this.updateLock(incumbentProductId, toProductId, locked);
  }

  updateLock(incumbentProductId, toProductId, locked) {
    let state = this.store.getState();

    this.api.purchasePlans.updateLock(
      state.activePurchasePlan.id,
      incumbentProductId,
      toProductId,
      locked,
    );

    this.store.dispatch({
      type: UPDATE_LOCK,
      data: {
        incumbentProductId,
        toProductId,
        locked,
      },
    });
  }

  filter(filterOptions: any) {
    // Perform a filter against our local data.
    this.store.dispatch({
      type: FILTER,
      data: filterOptions,
    });
  }

  isProductCrossPresent(productId, crossReferenceId) {
    let state = this.store.getState();
    return !!findProductCross(state, productId, crossReferenceId);
  }

  // This is the method call on click of thumbs up or down for the ppDetails grid
  voteOnClick(
    productId,
    crossReferenceId,
    vote,
    toProductId,
    rfpId,
    opinionOption?: CrossReferencesOpinionOption,
    fromDropdown?: boolean,
    useNewApi?: boolean,
  ) {
    let state = this.store.getState();
    let targetProductCross = findProductCross(
      state,
      productId,
      crossReferenceId,
    );

    if (targetProductCross !== null) {
      let currentOpinion = AptAssessmentVote.currentOpinion(
        0,
        0,
        targetProductCross.crossApproveCount,
        targetProductCross.crossDisputeCount,
        targetProductCross.notes,
        targetProductCross.crossOpinion,
        targetProductCross.parentOpinion,
        vote,
        state.account.emailAddress,
        opinionOption,
        fromDropdown,
      );

      this.store.dispatch({
        type: VOTE,
        data: {
          productId,
          crossReferenceId,
          currentOpinion,
        },
      });

      if (currentOpinion.targetProductCrossCrossOpinion === 'dispute') {
        // Clear any lock we might have on the product.
        this.store.dispatch({
          type: UPDATE_LOCK,
          data: {
            incumbentProductId: productId,
            toProductId,
            locked: false,
          },
        });

        // Make sure we remove any allocation which we might have to this product.
        this.removeAllocation(productId, toProductId);

        // MBV-706 - If the cross is a buyer cross, we are actually going to remove the product itself as a cross. They don't
        // want to see buyer crosses we've disputed from the available crosses.
        if (targetProductCross.crossReferenceCreatedByOrgType === 'Buyer') {
          this.store.dispatch({
            type: REMOVE_CROSS_FROM_PRODUCT,
            data: {
              incumbentProductId: productId,
              productToRemove: toProductId,
            },
          });
        }
      }

      if (useNewApi) {
        this.api.crossReferences.updateOpinion(
          crossReferenceId,
          opinionOption.optionKey,
          state.rfp.id,
          rfpId,
        );
      } else {
        this.api.crossReferences.updateVote(
          crossReferenceId,
          currentOpinion.targetProductCrossCrossOpinion,
          state.rfp.id,
          rfpId,
        );
      }
    }
  }

  addNoteOnClick(productId, crossReferenceId, note, toProductId) {
    let state = this.store.getState();
    let notes = {
      note: note,
      createdDate: new Date(),
      createdBy: state.account.emailAddress,
    };

    this.store.dispatch({
      type: ADD_NOTE,
      data: {
        productId,
        crossReferenceId,
        notes,
      },
    });

    this.api.crossReferences.addNote(crossReferenceId, note);
  }

  addCross(incumbentProductId: number, productCross) {
    let state = this.store.getState();

    // Look through all of our products (both incumbent and cross) and see if we've crossed this somewhere else
    // (which might mean that we have pricing for the product).
    let productsIterator = allProducts(state.products);

    for (let product of productsIterator) {
      // The yielded value includes the product (and possibly also an incumbent reference), as well as a done
      // indicator, and more. We're digging down to the product info buried in the yield value.
      if (product.product.productId === productCross.id) {
        productCross.unitPrice = product.product.unitPrice;
        productCross.pricingRequired = product.product.pricingRequired;
        break;
      }
    }

    this.store.dispatch({
      type: ADD_CROSS_TO_PRODUCT,
      data: {
        incumbentProductId: incumbentProductId,
        productCrossToAdd: productCross,
      },
    });
  }

  updateMixStatus(purchasePlanId) {
    let state = this.store.getState();
    this.api.purchasePlans
      .updateMixStatus(purchasePlanId, 'MODIFIED')
      .then((response) => {
        // Reload some of the same state we loaded when we first came up.
        this.fetchActivePurchasePlan(
          state.rfp.id,
          state.activePurchasePlan.memberId,
        );
      });
  }
}
